import React, { useState, useEffect } from 'react';
import { getEventHero } from '../lib/eventHero';
import { apiFetch } from '../lib/api';
import { TravelFormModal } from '../pages/Travel';
import { TimePicker, LocationSearch } from '../pages/SheetBuilder';
import PersonBioCard from './PersonBioCard';

function fmt12(t) {
  if (!t) return '';
  const [h, m] = t.split(':').map(Number);
  return `${h % 12 || 12}:${String(m).padStart(2,'0')} ${h >= 12 ? 'PM' : 'AM'}`;
}

function getDuration(start, end) {
  if (!start || !end) return null;
  const [sh, sm] = start.split(':').map(Number);
  const [eh, em] = end.split(':').map(Number);
  const dur = (eh * 60 + em) - (sh * 60 + sm);
  if (dur <= 0) return null;
  const hrs = Math.floor(dur / 60);
  const mins = dur % 60;
  return hrs > 0 ? `${hrs}h${mins > 0 ? ` ${mins}m` : ''}` : `${mins}m`;
}

function getFreeTime(endTime, nextStartTime) {
  if (!endTime || !nextStartTime) return null;
  const [eh, em] = endTime.split(':').map(Number);
  const [nh, nm] = nextStartTime.split(':').map(Number);
  const diff = (nh * 60 + nm) - (eh * 60 + em);
  if (diff <= 0) return null;
  const hrs = Math.floor(diff / 60);
  const mins = diff % 60;
  return hrs > 0 ? `${hrs}h ${mins > 0 ? mins + 'm ' : ''}free` : `${mins}m free`;
}

function InfoRow({ icon, iconColor, label, value, href, green, multiline }) {
  if (!value) return null;
  const Tag = href ? 'a' : 'div';
  return (
    <Tag href={href} target={href?.startsWith('http') ? '_blank' : undefined} rel="noreferrer"
      style={{ display:'flex', alignItems:'center', gap:14, padding:'14px 20px', borderBottom:'1px solid var(--border)', textDecoration:'none', cursor: href ? 'pointer' : 'default' }}>
      <div style={{ width:36, height:36, borderRadius:10, background:`${iconColor||'var(--blue)'}14`, display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0 }}>
        <i className={`fa-solid ${icon}`} style={{ fontSize:14, color: iconColor||'var(--blue)' }}></i>
      </div>
      <div style={{ flex:1, minWidth:0 }}>
        <p style={{ fontSize:11, fontWeight:600, color:'var(--text3)', letterSpacing:1, margin:0 }}>{label}</p>
        <p style={{ fontSize:15, fontWeight:600, color: green ? '#22c55e' : href ? 'var(--blue)' : 'var(--text)', margin:'2px 0 0',
          ...(multiline ? { whiteSpace:'pre-line', lineHeight:1.6 } : { overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }) }}>{value}</p>
      </div>
      {href && <i className="fa-solid fa-chevron-right" style={{ fontSize:11, color:'var(--text3)' }}></i>}
    </Tag>
  );
}

const inp = { width:'100%', background:'var(--bg)', border:'1px solid var(--border)', borderRadius:10, padding:'11px 14px', fontSize:14, color:'var(--text)', outline:'none', boxSizing:'border-box', fontFamily:'inherit' };

function EditForm({ event, onSave, onCancel }) {
  const [title, setTitle]   = useState(event.title || '');
  const [person, setPerson] = useState(event.person || '');
  const [tStart, setTStart] = useState(event.timeStart || '');
  const [tEnd, setTEnd]     = useState(event.timeEnd || '');
  const [loc, setLoc]       = useState(event.location || '');
  const [notes, setNotes]   = useState(event.notes || '');
  const [url, setUrl]       = useState(event.url || '');
  // null = regular event; non-null = travel-typed (drives badge/icon
  // on the published view + sheet timeline). Mirrors the toggle in
  // SheetBuilder's EventModal — without it on this form, edits made
  // from AdminToday / Viewer reverted travel events to plain ones.
  const [travelType, setTravelType] = useState(event.travelType || null);
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    if (!title.trim()) return;
    setSaving(true);
    try {
      const updated = await apiFetch(`/events/${event.id}`, {
        method: 'PATCH',
        body: JSON.stringify({
          title: title.trim(),
          person: person.trim() || null,
          timeStart: tStart || null,
          timeEnd: tEnd || null,
          location: loc.trim() || null,
          notes: notes.trim() || null,
          url: url.trim() || null,
          travelType: travelType || null,
        }),
      });
      onSave(updated);
    } catch (e) {
      alert(e.message);
    } finally {
      setSaving(false);
    }
  };

  const travelTypes = [
    { value: 'flight',     label: 'Flight' },
    { value: 'hotel',      label: 'Hotel' },
    { value: 'carservice', label: 'Car service' },
    { value: 'car',        label: 'Car rental' },
    { value: 'ground',     label: 'Ground' },
    { value: 'train',      label: 'Train' },
  ];

  const label = { fontSize:11, fontWeight:700, color:'var(--text3)', letterSpacing:0.5, display:'block', marginBottom:4, textTransform:'uppercase' };

  return (
    <div style={{ padding:'16px 20px 20px' }}>
      <div style={{ display:'flex', flexDirection:'column', gap:14 }}>
        <div>
          <label style={label}>Title</label>
          <input value={title} onChange={e => setTitle(e.target.value)} style={inp} autoFocus />
        </div>
        <div>
          <label style={label}>Person</label>
          <input value={person} onChange={e => setPerson(e.target.value)} placeholder="Everyone" style={inp} />
        </div>
        <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:10 }}>
          <div>
            <label style={label}>Start Time</label>
            <TimePicker value={tStart} onChange={setTStart} placeholder="Start time" />
          </div>
          <div>
            <label style={label}>End Time</label>
            <TimePicker value={tEnd} onChange={setTEnd} placeholder="End time" />
          </div>
        </div>
        <div>
          <label style={label}>Location</label>
          <LocationSearch value={loc} onChange={setLoc} />
        </div>
        <div>
          <label style={label}>URL / Link</label>
          <input value={url} onChange={e => setUrl(e.target.value)} placeholder="https://..." style={inp} />
        </div>
        <div>
          <label style={label}>Travel event</label>
          <div style={{ display:'flex', alignItems:'center', gap:10 }}>
            <button
              type="button"
              onClick={() => setTravelType(travelType ? null : 'flight')}
              aria-pressed={!!travelType}
              style={{
                position:'relative', width:44, height:26, borderRadius:13,
                background: travelType ? 'var(--blue)' : 'var(--bg3, #e5e7eb)',
                border:'none', cursor:'pointer', flexShrink:0,
                transition:'background 0.15s',
              }}
            >
              <span style={{
                position:'absolute', top:3, left: travelType ? 21 : 3,
                width:20, height:20, borderRadius:'50%',
                background:'#fff', transition:'left 0.15s',
                boxShadow:'0 1px 3px rgba(0,0,0,0.3)',
              }} />
            </button>
            {travelType ? (
              <select value={travelType} onChange={e => setTravelType(e.target.value)} style={{ ...inp, width:'auto', flex:1 }}>
                {travelTypes.map(t => (
                  <option key={t.value} value={t.value}>{t.label}</option>
                ))}
              </select>
            ) : (
              <span style={{ fontSize:13, color:'var(--text3)' }}>Off — regular event</span>
            )}
          </div>
        </div>
        <div>
          <label style={label}>Notes</label>
          <textarea value={notes} onChange={e => setNotes(e.target.value)} rows={3}
            style={{ ...inp, resize:'vertical' }} />
        </div>
        <div style={{ display:'flex', gap:10, paddingTop:4 }}>
          <button onClick={onCancel}
            style={{ flex:1, padding:'13px', borderRadius:12, background:'var(--bg)', border:'1px solid var(--border)', color:'var(--text2)', fontFamily:'inherit', fontSize:14, fontWeight:700, cursor:'pointer' }}>
            Cancel
          </button>
          <button onClick={handleSave} disabled={saving || !title.trim()}
            style={{ flex:2, padding:'13px', borderRadius:12, background:'linear-gradient(135deg,#1E40AF,#1E40AF)', border:'none', color:'#fff', fontFamily:'inherit', fontSize:14, fontWeight:700, cursor:'pointer', opacity: (saving || !title.trim()) ? 0.5 : 1 }}>
            {saving ? 'Saving...' : 'Save Changes'}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function EventModal({ event, sheet, prevEvent, nextEvent, colorMap = {}, onClose, isAdmin, onEventUpdated }) {
  const [editing, setEditing] = useState(false);
  const [currentEvent, setCurrentEvent] = useState(event);
  const [shareOpen, setShareOpen] = useState(false);
  const [shareEmail, setShareEmail] = useState('');
  const [shareSending, setShareSending] = useState(false);
  const [shareMsg, setShareMsg] = useState('');
  const [travelRecord, setTravelRecord] = useState(null);
  const [travelLoading, setTravelLoading] = useState(false);

  // Reset when event changes
  React.useEffect(() => { setCurrentEvent(event); setEditing(false); setTravelRecord(null); }, [event]);

  // Fetch travel record when editing a travel-linked event
  const isTravelEvent = !!(currentEvent?.travelId);
  const handleTravelEdit = async () => {
    if (!currentEvent.travelId) { setEditing(true); return; }
    setTravelLoading(true);
    try {
      const records = await apiFetch(`/travel?artistId=${currentEvent.artistId}`);
      const found = (Array.isArray(records) ? records : []).find(r => r.id === currentEvent.travelId);
      if (found) { setTravelRecord(found); } else { setEditing(true); }
    } catch { setEditing(true); }
    finally { setTravelLoading(false); }
  };

  if (!currentEvent) return null;

  const hero = getEventHero(currentEvent.title, currentEvent.location, currentEvent.url);
  const hasMap = currentEvent.location && hero.type === 'map';
  const hasImage = hero.imageUrl;
  const hasBrand = hero.type === 'brand';
  const hasKeywordGradient = hero.type === 'keyword' && !hasImage;
  const hasHero = hasMap || hasImage || hasBrand || hasKeywordGradient;

  const isZoom  = currentEvent.url?.includes('zoom.us');
  const isMeet  = currentEvent.url?.includes('meet.google.com');
  const isTeams = currentEvent.url?.includes('teams.microsoft.com') || currentEvent.url?.includes('teams.live.com');
  const linkLabel = isZoom ? 'Join Zoom' : isMeet ? 'Join Meet' : isTeams ? 'Join Teams' : 'Open Link';
  const linkColor = isZoom ? '#2D8CFF' : isMeet ? '#34A853' : isTeams ? '#6264A7' : 'var(--blue)';
  const linkBg    = isZoom ? 'rgba(45,140,255,0.15)' : isMeet ? 'rgba(52,168,83,0.15)' : isTeams ? 'rgba(98,100,167,0.15)' : 'rgba(79,156,249,0.15)';
  const linkBdr   = isZoom ? '1px solid rgba(45,140,255,0.4)' : isMeet ? '1px solid rgba(52,168,83,0.4)' : isTeams ? '1px solid rgba(98,100,167,0.4)' : '1px solid rgba(79,156,249,0.4)';

  const duration = getDuration(currentEvent.timeStart, currentEvent.timeEnd);
  const freeTime = getFreeTime(currentEvent.timeEnd, nextEvent?.timeStart);
  const dateStr = sheet?.date ? new Date(sheet.date + 'T12:00:00').toLocaleDateString('en-US', { weekday:'long', month:'long', day:'numeric' }) : '';

  const persons = (currentEvent.person||'').split(',').map(p=>p.trim()).filter(Boolean);
  const firstPersonKey = persons.length ? Object.keys(colorMap).find(k => k.toLowerCase() === persons[0].toLowerCase()) : null;
  const accentColor = firstPersonKey ? colorMap[firstPersonKey] : 'var(--blue)';

  const noteLines = (currentEvent.notes||'').split('\n').map(l => l.trim()).filter(Boolean);
  const contactLines = noteLines.filter(l => /^(Driver|Greeter):/i.test(l));
  const otherNotes = noteLines.filter(l => !/^(Driver|Greeter):/i.test(l)).join('\n');

  const handleEditSave = (updated) => {
    setCurrentEvent(prev => ({ ...prev, ...updated }));
    setEditing(false);
    if (onEventUpdated) onEventUpdated(updated);
  };

  // Travel form modal — shown when editing a travel-linked event
  if (travelRecord) {
    return (
      <TravelFormModal
        record={travelRecord}
        artistId={currentEvent.artistId}
        onSave={(result) => {
          // Backend returns updated linked events — find ours and refresh
          const updatedEvent = (result.events || []).find(e => e.id === currentEvent.id);
          if (updatedEvent) {
            setCurrentEvent(prev => ({ ...prev, ...updatedEvent }));
            if (onEventUpdated) onEventUpdated(updatedEvent);
          }
          setTravelRecord(null);
        }}
        onClose={() => setTravelRecord(null)}
      />
    );
  }

  return (
    <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.8)', zIndex:2000, display:'flex', alignItems:'flex-end', justifyContent:'center' }}
      onMouseDown={e => e.target === e.currentTarget && onClose()}>
      <button onClick={onClose}
        style={{ position:'absolute', top:16, left:16, width:36, height:36, borderRadius:'50%',
          background:'rgba(255,255,255,0.12)', backdropFilter:'blur(10px)', WebkitBackdropFilter:'blur(10px)',
          border:'none', cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'center', zIndex:2010 }}>
        <i className="fa-solid fa-xmark" style={{ fontSize:16, color:'#fff' }}></i>
      </button>
      <div style={{ background:'var(--card)', borderRadius:'20px 20px 0 0', width:'100%', maxWidth:480, maxHeight:'92vh', overflowY:'auto', border:'1px solid var(--border)', borderBottom:'none', paddingBottom:'calc(88px + env(safe-area-inset-bottom, 0px))' }}>

        {/* Handle */}
        <div style={{ position:'sticky', top:0, zIndex:2, background:'var(--card)', padding:'10px 0 0', borderRadius:'20px 20px 0 0' }}>
          <div style={{ width:36, height:4, background:'var(--border)', borderRadius:2, margin:'0 auto' }} />
        </div>

        {/* ── EDIT MODE ── */}
        {editing ? (
          <>
            <div style={{ padding:'16px 20px 0', display:'flex', justifyContent:'space-between', alignItems:'center' }}>
              <h3 style={{ fontSize:18, fontWeight:800, color:'var(--text)', margin:0 }}>Edit Event</h3>
            </div>
            <EditForm event={currentEvent} onSave={handleEditSave} onCancel={() => setEditing(false)} />
          </>
        ) : (
          <>
            {/* Hero */}
            {hasHero && (
              <div style={{ height: 180, position:'relative', overflow:'hidden' }}>
                {hasMap ? (
                  <iframe title="Map" src={`https://www.google.com/maps?q=${encodeURIComponent(currentEvent.location)}&output=embed&z=14`}
                    style={{ width:'100%', height:'100%', border:'none', filter:'brightness(0.8) contrast(1.1) saturate(0.7)', pointerEvents:'none' }} loading="lazy" />
                ) : hasImage ? (
                  <div style={{ width:'100%', height:'100%', backgroundImage:`url(${hero.imageUrl})`, backgroundSize:'cover', backgroundPosition:'center', filter:'brightness(0.5) saturate(0.8)' }} />
                ) : hasBrand ? (
                  <div style={{ width:'100%', height:'100%', background:hero.gradient, display:'flex', alignItems:'center', justifyContent:'center' }}>
                    <i className={`fa-solid ${hero.icon}`} style={{ fontSize:48, color:'rgba(255,255,255,0.12)' }}></i>
                  </div>
                ) : hasKeywordGradient ? (
                  <div style={{ width:'100%', height:'100%', background:hero.gradient, display:'flex', alignItems:'center', justifyContent:'center' }}>
                    <i className={`fa-solid ${hero.icon}`} style={{ fontSize:48, color:'rgba(255,255,255,0.12)' }}></i>
                  </div>
                ) : null}
                <div style={{ position:'absolute', bottom:0, left:0, right:0, height:60, background:'linear-gradient(transparent, var(--card))' }} />
                {hasBrand && (
                  <div style={{ position:'absolute', bottom:14, left:20, display:'flex', alignItems:'center', gap:8 }}>
                    <i className={`fa-solid ${hero.icon}`} style={{ fontSize:16, color:'white' }}></i>
                    <span style={{ fontSize:14, fontWeight:800, color:'white' }}>{hero.label}</span>
                  </div>
                )}
                {hasMap && (
                  <a href={`https://maps.apple.com/?q=${encodeURIComponent(currentEvent.location)}`} target="_blank" rel="noreferrer"
                    style={{ position:'absolute', top:12, right:12, background:'rgba(0,0,0,0.5)', backdropFilter:'blur(10px)', borderRadius:8, padding:'6px 10px', display:'flex', alignItems:'center', gap:4, textDecoration:'none' }}>
                    <i className="fa-solid fa-diamond-turn-right" style={{ fontSize:10, color:'white' }}></i>
                    <span style={{ fontSize:11, fontWeight:700, color:'white' }}>Directions</span>
                  </a>
                )}
              </div>
            )}

            {/* Header */}
            <div style={{ padding:'20px 20px 16px', borderBottom:'1px solid var(--border)' }}>
              <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:6 }}>
                <span style={{ fontSize:10, fontWeight:700, letterSpacing:2, color:'var(--text3)', textTransform:'uppercase' }}>
                  {sheet?.artist?.name}{sheet?.date ? ` \u00b7 ${new Date(sheet.date+'T12:00:00').toLocaleDateString('en-US',{month:'short',day:'numeric'})}` : ''}
                </span>
                {isAdmin && (
                  <button onClick={isTravelEvent ? handleTravelEdit : () => setEditing(true)} disabled={travelLoading}
                    style={{ display:'inline-flex', alignItems:'center', gap:5, padding:'4px 12px', borderRadius:8, background:'rgba(30,64,175,0.1)', border:'1px solid rgba(30,64,175,0.25)', color:'var(--blue)', fontSize:12, fontWeight:700, cursor:'pointer', fontFamily:'inherit', opacity: travelLoading ? 0.5 : 1 }}>
                    <i className="fa-solid fa-pen" style={{ fontSize:10 }}></i> {travelLoading ? '...' : 'Edit'}
                  </button>
                )}
              </div>
              <h2 style={{ fontSize:22, fontWeight:900, color:'var(--text)', margin:'0 0 8px', lineHeight:1.2, letterSpacing:-0.5 }}>{currentEvent.title}</h2>
              <div style={{ display:'flex', alignItems:'center', gap:8, flexWrap:'wrap' }}>
                <span style={{ fontSize:15, color:'var(--text2)', fontWeight:600 }}>
                  {currentEvent.timeStart ? fmt12(currentEvent.timeStart) : 'All day'}
                  {currentEvent.timeEnd ? ` \u2013 ${fmt12(currentEvent.timeEnd)}` : ''}
                </span>
                {duration && <span style={{ fontSize:12, color:'var(--text3)', background:'rgba(255,255,255,0.04)', borderRadius:8, padding:'2px 10px', border:'1px solid var(--border)' }}>{duration}</span>}
              </div>
              <p style={{ fontSize:13, color:'var(--text3)', margin:'6px 0 0' }}>{dateStr}</p>

              {persons.length > 0 && (
                <div style={{ display:'flex', gap:6, marginTop:10, flexWrap:'wrap' }}>
                  {persons.map((p,i) => {
                    const key = Object.keys(colorMap).find(k => k.toLowerCase() === p.toLowerCase());
                    const c = key ? colorMap[key] : '#94a3b8';
                    return <span key={i} style={{ display:'inline-flex', alignItems:'center', gap:5, padding:'3px 10px', borderRadius:16, background:c+'14', border:`1px solid ${c}30`, color:c, fontSize:12, fontWeight:700 }}><span style={{ width:6, height:6, borderRadius:'50%', background:c }} />{p}</span>;
                  })}
                </div>
              )}
            </div>

            {/* Action buttons */}
            <div style={{ display:'flex', gap:8, padding:'16px 20px', borderBottom:'1px solid var(--border)' }}>
              {currentEvent.location && (
                <a href={`https://maps.apple.com/?q=${encodeURIComponent(currentEvent.location)}`} target="_blank" rel="noreferrer"
                  style={{ flex:1, display:'flex', alignItems:'center', justifyContent:'center', gap:8, padding:'12px', borderRadius:12, background:'var(--blue)', color:'white', fontWeight:700, fontSize:14, textDecoration:'none', fontFamily:'inherit' }}>
                  <i className="fa-solid fa-diamond-turn-right" style={{ fontSize:12 }}></i> Directions
                </a>
              )}
              {currentEvent.url && (
                <a href={currentEvent.url} target="_blank" rel="noreferrer"
                  style={{ flex:1, display:'flex', alignItems:'center', justifyContent:'center', gap:8, padding:'12px', borderRadius:12,
                    background:linkBg, border:linkBdr, color:linkColor, fontWeight:700, fontSize:14, textDecoration:'none', fontFamily:'inherit' }}>
                  <i className={`fa-solid ${isZoom?'fa-video':isMeet?'fa-video':isTeams?'fa-headset':'fa-link'}`} style={{ fontSize:12 }}></i> {linkLabel}
                </a>
              )}
              <button onClick={() => {
                const e = currentEvent;
                const lines = [];
                lines.push(`*${e.title}*`);
                lines.push(`${dateStr}`);
                if (e.timeStart) lines.push(`${fmt12(e.timeStart)}${e.timeEnd ? ' – ' + fmt12(e.timeEnd) : ''}${duration ? ' (' + duration + ')' : ''}`);
                if (persons.length) lines.push(persons.join(', '));
                lines.push('');
                if (e.location) lines.push(`📍 ${e.location}`);
                if (e.location) lines.push(`https://maps.apple.com/?q=${encodeURIComponent(e.location)}`);
                if (e.url) { lines.push(''); lines.push(`🔗 ${linkLabel}: ${e.url}`); }
                if (otherNotes) { lines.push(''); lines.push(otherNotes); }
                contactLines.forEach(l => { lines.push(''); lines.push(l); });
                const text = lines.filter(l => l !== undefined).join('\n');
                if (navigator.share) {
                  navigator.share({ title: e.title, text }).catch(() => {});
                } else {
                  navigator.clipboard.writeText(text);
                }
              }}
                style={{ display:'flex', alignItems:'center', justifyContent:'center', gap:6, padding:'12px 16px', borderRadius:12,
                  background:'rgba(34,197,94,0.08)', border:'1px solid rgba(34,197,94,0.2)', color:'#22c55e', fontWeight:700, fontSize:14, cursor:'pointer', fontFamily:'inherit', flexShrink:0 }}>
                <i className="fa-solid fa-share-from-square" style={{ fontSize:13 }}></i>
              </button>
              <button onClick={() => { setShareOpen(!shareOpen); setShareEmail(''); setShareMsg(''); }}
                style={{ display:'flex', alignItems:'center', justifyContent:'center', gap:6, padding:'12px 16px', borderRadius:12,
                  background: shareOpen ? 'var(--blue)' : 'rgba(30,64,175,0.08)', border: shareOpen ? 'none' : '1px solid rgba(30,64,175,0.2)', color: shareOpen ? '#fff' : 'var(--blue)', fontWeight:700, fontSize:14, cursor:'pointer', fontFamily:'inherit', flexShrink:0 }}>
                <i className="fa-solid fa-paper-plane" style={{ fontSize:13 }}></i>
              </button>
            </div>

            {/* Share email input */}
            {shareOpen && (
              <div style={{ padding:'12px 20px', borderBottom:'1px solid var(--border)', background:'rgba(30,64,175,0.03)' }}>
                <p style={{ fontSize:11, fontWeight:700, color:'var(--text3)', marginBottom:8, letterSpacing:0.5 }}>SEND EVENT CARD VIA EMAIL</p>
                <div style={{ display:'flex', gap:8 }}>
                  <input value={shareEmail} onChange={e => setShareEmail(e.target.value)} placeholder="email@example.com"
                    onKeyDown={e => e.key === 'Enter' && document.getElementById('share-send-btn')?.click()}
                    style={{ flex:1, background:'var(--bg)', border:'1px solid var(--border)', borderRadius:10, padding:'10px 12px', fontSize:14, color:'var(--text)', outline:'none', boxSizing:'border-box', fontFamily:'inherit' }} />
                  <button id="share-send-btn" onClick={async () => {
                    if (!shareEmail.includes('@') || !currentEvent.id) return;
                    setShareSending(true); setShareMsg('');
                    try {
                      const res = await apiFetch(`/events/${currentEvent.id}/share`, { method:'POST', body: JSON.stringify({ to: shareEmail }) });
                      const sent = (res.results || []).filter(r => r.status === 'sent').length;
                      setShareMsg(sent > 0 ? 'Sent!' : 'Failed to send');
                      if (sent > 0) setShareEmail('');
                    } catch(e) { setShareMsg('Error: ' + e.message); }
                    finally { setShareSending(false); }
                  }} disabled={shareSending || !shareEmail.includes('@')}
                    style={{ padding:'10px 18px', borderRadius:10, background: shareEmail.includes('@') ? 'linear-gradient(135deg,#1E40AF,#1E40AF)' : 'var(--bg)', border: shareEmail.includes('@') ? 'none' : '1px solid var(--border)', color: shareEmail.includes('@') ? '#fff' : 'var(--text3)', fontFamily:'inherit', fontSize:13, fontWeight:700, cursor: shareEmail.includes('@') ? 'pointer' : 'default' }}>
                    {shareSending ? '...' : 'Send'}
                  </button>
                </div>
                {shareMsg && <p style={{ fontSize:12, color: shareMsg === 'Sent!' ? '#22c55e' : '#ef4444', marginTop:6, fontWeight:600 }}>{shareMsg}</p>}
              </div>
            )}

            {/* Pillar 1: auto-bio surface. Renders only when the
                title looks like it references a person, so it stays
                out of the way for soundcheck/show/etc. Open to any
                authenticated viewer — the use case (artist prepping
                for a Zoom) is broader than admins. Cost is held in
                check by the 30-day server-side bio cache. */}
            <div style={{ padding: '12px 20px 0' }}>
              <PersonBioCard title={currentEvent.title} notes={currentEvent.notes} />
            </div>

            {/* Info rows */}
            <div>
              <InfoRow icon="fa-location-dot" label="LOCATION" value={currentEvent.location}
                href={currentEvent.location ? `https://maps.apple.com/?q=${encodeURIComponent(currentEvent.location)}` : undefined} />
              <InfoRow icon="fa-note-sticky" iconColor="var(--text3)" label="NOTES" value={otherNotes} multiline />

              {contactLines.map((line, i) => {
                const isDriver = /^Driver:/i.test(line);
                const val = line.replace(/^(Driver|Greeter):\s*/i, '');
                const phoneMatch = val.match(/(\+?1?[\s\-.]?\(?[0-9]{3}\)?[\s\-.][0-9]{3}[\s\-.][0-9]{4}|[0-9]{10,11})/);
                const phone = phoneMatch ? phoneMatch[0].trim() : null;
                const name = phone ? val.replace(phone, '').replace(/[\s\-·,]+$/, '').trim() : val;
                return (
                  <InfoRow key={i}
                    icon={isDriver ? 'fa-car' : 'fa-hand-wave'}
                    iconColor={isDriver ? 'var(--blue)' : '#fbbf24'}
                    label={isDriver ? 'DRIVER' : 'GREETER'}
                    value={`${name||val}${phone ? ' \u00b7 '+phone : ''}`}
                    href={phone ? `tel:${phone.replace(/\D/g,'')}` : undefined}
                    green={!!phone}
                  />
                );
              })}

              {currentEvent.gcalEventId && (
                <InfoRow icon="fa-circle-check" iconColor="#22c55e" label="GOOGLE CALENDAR" value="Synced" green />
              )}
            </div>

            {/* Timeline — current event + free time + next event */}
            {nextEvent && (
              <div style={{ borderTop:'1px solid var(--border)' }}>
                <div style={{ padding:'14px 20px 8px' }}>
                  <p style={{ fontSize:10, fontWeight:700, letterSpacing:2, color:'var(--text3)', margin:0 }}>
                    <i className="fa-regular fa-clock" style={{ marginRight:5, fontSize:9 }}></i>UP NEXT
                  </p>
                </div>
                <div style={{ display:'flex', alignItems:'center', gap:12, padding:'8px 20px', background:'rgba(30,64,175,0.04)', borderLeft:`3px solid ${accentColor}` }}>
                  <span style={{ fontSize:11, color:accentColor, minWidth:53, fontWeight:700, textAlign:'right' }}>{currentEvent.timeStart?fmt12(currentEvent.timeStart):''}</span>
                  <div style={{ width:7, height:7, borderRadius:'50%', background:accentColor, flexShrink:0 }} />
                  <span style={{ fontSize:13, color:'var(--text)', fontWeight:700 }}>{currentEvent.title}</span>
                </div>
                {freeTime && (
                  <div style={{ padding:'3px 20px 3px 76px' }}>
                    <span style={{ fontSize:11, color:'#22c55e', fontWeight:600 }}><i className="fa-solid fa-arrow-down" style={{ fontSize:8, marginRight:4 }}></i>{freeTime}</span>
                  </div>
                )}
                <div style={{ display:'flex', alignItems:'center', gap:12, padding:'6px 20px 10px' }}>
                  <span style={{ fontSize:11, color:'var(--text3)', minWidth:56, fontWeight:600, textAlign:'right' }}>{nextEvent.timeStart?fmt12(nextEvent.timeStart):''}</span>
                  <div style={{ width:7, height:7, borderRadius:'50%', background:'var(--text3)', flexShrink:0 }} />
                  <span style={{ fontSize:12, color:'var(--text)', fontWeight:600 }}>{nextEvent.title}</span>
                </div>
              </div>
            )}

            {/* Contacts */}
            {currentEvent.contacts?.filter(c => c.name || c.phone).length > 0 && (
              <div style={{ borderTop:'1px solid var(--border)' }}>
                <div style={{ padding:'14px 20px 6px' }}>
                  <p style={{ fontSize:10, fontWeight:700, letterSpacing:2, color:'var(--text3)', margin:0 }}>
                    <i className="fa-regular fa-address-book" style={{ marginRight:5, fontSize:9 }}></i>CONTACTS
                  </p>
                </div>
                {currentEvent.contacts.filter(c => c.name || c.phone).map((c, i) => (
                  <a key={i} href={c.phone?`tel:${c.phone.replace(/\D/g,'')}`:'#'}
                    style={{ display:'flex', alignItems:'center', gap:14, padding:'10px 20px', textDecoration:'none' }}>
                    <div style={{ width:36, height:36, borderRadius:'50%', background:'rgba(30,64,175,0.08)', display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0 }}>
                      <i className="fa-solid fa-user" style={{ fontSize:12, color:'var(--blue)' }}></i>
                    </div>
                    <div style={{ flex:1 }}>
                      <p style={{ fontSize:14, fontWeight:600, color:'var(--text)', margin:0 }}>{c.name}</p>
                    </div>
                    {c.phone && <span style={{ fontSize:12, fontWeight:700, color:'#22c55e' }}>{c.phone}</span>}
                  </a>
                ))}
              </div>
            )}

            {/* Files */}
            {currentEvent.files?.length > 0 && (
              <div style={{ borderTop:'1px solid var(--border)', padding:'14px 20px' }}>
                <p style={{ fontSize:10, fontWeight:700, letterSpacing:2, color:'var(--text3)', marginBottom:10 }}>
                  <i className="fa-solid fa-paperclip" style={{ marginRight:5, fontSize:9 }}></i>ATTACHMENTS
                </p>
                <div style={{ display:'flex', gap:8, overflowX:'auto' }}>
                  {currentEvent.files.map((f, i) => {
                    const openFile = () => {
                      if (f.url) { window.open((f.url.startsWith('http')?'':process.env.REACT_APP_API_URL||'http://localhost:3001')+f.url, '_blank'); return; }
                      if (f.data) { const a=f.data.split(','); const m=a[0].match(/:(.*?);/)[1]; const b=atob(a[1]); const u=new Uint8Array(b.length); for(let j=0;j<b.length;j++) u[j]=b.charCodeAt(j); window.open(URL.createObjectURL(new Blob([u],{type:m})),'_blank'); }
                    };
                    return (
                      <button key={i} onClick={openFile}
                        style={{ flexShrink:0, display:'flex', alignItems:'center', gap:8, padding:'10px 14px', background:'var(--bg)', borderRadius:10, border:'1px solid var(--border)', cursor:'pointer', fontFamily:'inherit' }}>
                        <i className={`fa-solid ${f.type?.includes('pdf')?'fa-file-pdf':f.type?.startsWith('image/')?'fa-image':'fa-file'}`} style={{ fontSize:16, color:accentColor }}></i>
                        <span style={{ fontSize:12, fontWeight:600, color:'var(--text)' }}>{f.name?.replace(/\.[^.]+$/,'')||'File'}</span>
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Close */}
            <div style={{ padding:'16px 20px 36px' }}>
              <button onClick={onClose}
                style={{ width:'100%', padding:'14px', borderRadius:14, background:'var(--bg)', border:'1px solid var(--border)', color:'var(--text2)', fontFamily:'inherit', fontSize:15, fontWeight:700, cursor:'pointer' }}>
                Close
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
